import React, { FC, useCallback, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import Dialog from "legacy-components/dialog/Dialog";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ApplicationService from "../../../applications/services/ApplicationService";
import ApplicationServiceV2 from "../../../applications/components/v2.0/ApplicationService";

import type { IdentityApplicationViewState } from "libs/resources/identity/applications/application-views/IdentityApplicationInfo";

export interface UploadCSRDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload?: (application?: IdentityApplicationViewState["application"]) => void;
  application?: IdentityApplicationViewState["application"];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(1, 1, 0),
      width: "100%",
      maxWidth: 720,
    },
    intro: {
      color: "#6B7785",
      lineHeight: 1.45,
      marginBottom: theme.spacing(3),
      fontSize: theme.typography.pxToRem(18),
      [theme.breakpoints.down("sm")]: {
        minWidth: "auto",
        fontSize: theme.typography.pxToRem(16),
      },
    },
    label: {
      fontWeight: theme.typography.fontWeightMedium,
      color: "#31363F",
      marginBottom: theme.spacing(1.5),
      fontSize: theme.typography.pxToRem(16),
      [theme.breakpoints.down("sm")]: {
        fontSize: theme.typography.pxToRem(16),
      },
    },
    required: {
      color: "#D14343",
    },
    certificateInput: {
      marginBottom: theme.spacing(3),
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
      },
      "& .MuiOutlinedInput-input": {
        padding: theme.spacing(1.8, 2.5),
        fontSize: theme.typography.pxToRem(20),
      },
      [theme.breakpoints.down("sm")]: {
        "& .MuiOutlinedInput-input": {
          fontSize: theme.typography.pxToRem(16),
        },
      },
    },
    helperText: {
      color: "#6B7785",
      marginBottom: theme.spacing(2),
      fontSize: theme.typography.pxToRem(13),
    },
    infoAlert: {
      border: "1px solid #8FC2EE",
      backgroundColor: "#D9ECFB",
      marginBottom: theme.spacing(3),
      "& .MuiAlert-message": {
        width: "100%",
      },
    },
    infoTitle: {
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(0.5),
    },
    infoContentRow: {
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-start",
      columnGap: theme.spacing(1.5),
    },
    infoTextBlock: {
      paddingTop: 1,
    },
    link: {
      color: "#0063A3",
      textDecoration: "underline",
    },
    actions: {
      borderTop: "1px solid #E1E6EF",
      margin: theme.spacing(0, -3, 0),
      padding: theme.spacing(2.5, 3, 2),
      display: "flex",
      justifyContent: "flex-end",
      gap: theme.spacing(1.5),
    },
    bannerIcon: {
      color: "#0b5d87",
      flexShrink: 0,
      marginTop: 2,
    },
  })
);

const UploadCSRDialog: FC<UploadCSRDialogProps> = ({ open, onClose, onUpload, application }) => {
  const classes = useStyles();
  const [certificateText, setCertificateText] = useState<string>("");
  const [uploadError, setUploadError] = useState<string>("");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isCertificateValid, setIsCertificateValid] = useState<boolean>(false);

  const handleTextChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCertificateText(event.target.value);
    const { isValid, error } = validatePemSyntax(event.target.value);
    if (!isValid) {
      setIsCertificateValid(false);
    } else if (isValid) {
      setIsCertificateValid(true);
    }
      if (uploadError && event.target.value.trim().length > 0) {
        setUploadError("");
      }
  }, [uploadError]);

  const validatePemSyntax = (certString: string): { isValid: boolean; error?: string } => {
  if (!certString || typeof certString !== 'string') {
    return { isValid: false, error: 'Certificate input is required.' };
  }

  const trimmed = certString.trim();

  if (!trimmed.startsWith('-----BEGIN CERTIFICATE REQUEST-----')) {
    return { isValid: false, error: 'Must begin with -----BEGIN CERTIFICATE REQUEST-----' };
  }

  if (!trimmed.endsWith('-----END CERTIFICATE REQUEST-----')) {
    return { isValid: false, error: 'Must end with -----END CERTIFICATE REQUEST-----' };
  }

  const base64Body = trimmed
    .replace(/-----BEGIN CERTIFICATE REQUEST-----/g, '')
    .replace(/-----END CERTIFICATE REQUEST-----/g, '')
    .replace(/[\r\n\s]/g, '');

  if (base64Body.length === 0) {
    return { isValid: false, error: 'Certificate body cannot be empty.' };
  }

  return { isValid: true };
};

  const handleClose = useCallback(() => {
    setUploadError("");
    setCertificateText("");
    onClose();
    setIsCertificateValid(false);
  }, [onClose]);

  const handleUpload = useCallback(async () => {
    const trimmedCertificateText = certificateText.trim();
    const normalizedCertificateText = trimmedCertificateText
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n");
    const applicationName = application?.applicationName;
    const organisationName = application?.organisationName;
    const consumerKey = application?.applicationGatewayResponses?.[0]?.consumerKey;

    if (!trimmedCertificateText) {
      setUploadError("Certificate text is required");
      return;
    }

    if (!applicationName || !organisationName) {
      setUploadError("Application details are required to upload the certificate");
      return;
    }

    try {
      setIsUploading(true);
      setUploadError("");
      await ApplicationServiceV2.addCertificate(consumerKey ?? "", { csr: normalizedCertificateText });
      const refreshedApplication = await new Promise<IdentityApplicationViewState["application"]>((resolve, reject) => {
        ApplicationService.get(organisationName, applicationName).subscribe(resolve, reject);
      });
      onUpload?.(refreshedApplication);
      setIsCertificateValid(true)
      handleClose();
    } catch (error) {
      const errorMessage =
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof (error as { response?: { data?: { message?: string } } }).response?.data?.message === "string"
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ?? "Failed to upload certificate"
          : "Failed to upload certificate";

      setUploadError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  }, [application, certificateText, handleClose, onUpload]);

  return (
    <Dialog open={open} onClose={handleClose} title="Upload CSR" titleSeparator maxWidth="sm">
      <Box className={classes.container}>
        <Typography className={classes.intro}>
          Upload an issued X.509 certificate for this application. Certificates are used for Mutual TLS
          (mTLS) authentication with minimum security instead of client secrets.
        </Typography>

        <Typography className={classes.label}>
          Certificate text <span className={classes.required}>*</span>
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          multiline={true}
          rows={5}
          placeholder="Add your certificate text here"
          value={certificateText}
          onChange={handleTextChange}
          className={classes.certificateInput}
        />

        {uploadError && (
          <Alert severity="error" variant="outlined" style={{ marginBottom: 16 }}>
            {uploadError}
          </Alert>
        )}

        <Alert icon={false} className={classes.infoAlert}>
          <Box className={classes.infoContentRow}>
            <InfoOutlinedIcon className={classes.bannerIcon} />
            <Box className={classes.infoTextBlock}>
              <Typography className={classes.infoTitle}>Don't have a certificate yet?</Typography>
              <Typography variant="body2" style={{ color: "#264053" }}>
                Generate a CSR and request a certificate from trial.
                <span> </span>
                <a
                  href="https://docs.trimblecloud.com/trimble-identity/content/how-to-guides/application_authentication/tid-mTLS-auth-client-credentials-grant/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.link}
                >
                  <br />
                  Learn how: Trimble Trusted Authentication
                </a>
              </Typography>
            </Box>
          </Box>
        </Alert>
        <Box className={classes.actions}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!isCertificateValid || isUploading}
            onClick={handleUpload}
          >
            {isUploading ? "Uploading..." : "Submit"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default UploadCSRDialog;
